import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  getScraperCategories,
  getScraperSubcategories,
  createScraperCategory,
  updateScraperCategory,
  deleteScraperCategory,
  createScraperSubcategory,
  updateScraperSubcategory,
  deleteScraperSubcategory,
} from "../services/api";

import {
  AppButton,
  AppInput,
  AppSelect,
  SectionCard,
  DataTable,
  ConfirmModal,
} from "../components/scraper/index";

// =============================================================================
const ScraperCategories = () => {
  // ── Data ──────────────────────────────────────────────────────────────────
  const [categories,    setCategories]    = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  // ── Category form ─────────────────────────────────────────────────────────
  const [categoryName,      setCategoryName]      = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [saveCatLoading,    setSaveCatLoading]     = useState(false);

  // ── Subcategory form ──────────────────────────────────────────────────────
  const [subcategoryName,      setSubcategoryName]      = useState("");
  const [selectedCategory,     setSelectedCategory]     = useState("");
  const [editingSubcategoryId, setEditingSubcategoryId] = useState(null);
  const [saveSubLoading,       setSaveSubLoading]        = useState(false);

  // ── Delete confirmations ──────────────────────────────────────────────────
  const [deleteCatTarget, setDeleteCatTarget] = useState(null);  // { id, name }
  const [deleteSubTarget, setDeleteSubTarget] = useState(null);  // { id, name }
  const [deleteCatLoading, setDeleteCatLoading] = useState(false);
  const [deleteSubLoading, setDeleteSubLoading] = useState(false);

  // ── Page-level load ───────────────────────────────────────────────────────
  const [pageLoading, setPageLoading] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────
  const loadData = async () => {
    try {
      setPageLoading(true);
      const [catRes, subRes] = await Promise.all([
        getScraperCategories(),
        getScraperSubcategories(),
      ]);
      setCategories(catRes?.data    || []);
      setSubcategories(subRes?.data || []);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // ── Category CRUD ─────────────────────────────────────────────────────────
  const handleSaveCategory = async () => {
    if (!categoryName.trim()) { toast.error("Category name required"); return; }
    try {
      setSaveCatLoading(true);
      if (editingCategoryId) {
        await updateScraperCategory(editingCategoryId, categoryName);
        toast.success("Category updated");
      } else {
        await createScraperCategory(categoryName);
        toast.success("Category created");
      }
      setCategoryName("");
      setEditingCategoryId(null);
      await loadData();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Operation failed");
    } finally {
      setSaveCatLoading(false);
    }
  };

  const handleCancelCategoryEdit = () => {
    setCategoryName("");
    setEditingCategoryId(null);
  };

  const confirmDeleteCategory = async () => {
    if (!deleteCatTarget) return;
    try {
      setDeleteCatLoading(true);
      await deleteScraperCategory(deleteCatTarget.id);
      toast.success("Category deleted");
      setDeleteCatTarget(null);
      await loadData();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleteCatLoading(false);
    }
  };

  // ── Subcategory CRUD ──────────────────────────────────────────────────────
  const handleSaveSubcategory = async () => {
    if (!selectedCategory)      { toast.error("Select a category");   return; }
    if (!subcategoryName.trim()) { toast.error("Subcategory name required"); return; }
    try {
      setSaveSubLoading(true);
      if (editingSubcategoryId) {
        await updateScraperSubcategory(editingSubcategoryId, selectedCategory, subcategoryName);
        toast.success("Subcategory updated");
      } else {
        await createScraperSubcategory(selectedCategory, subcategoryName);
        toast.success("Subcategory created");
      }
      setSubcategoryName("");
      setSelectedCategory("");
      setEditingSubcategoryId(null);
      await loadData();
    } catch {
      toast.error("Operation failed");
    } finally {
      setSaveSubLoading(false);
    }
  };

  const handleCancelSubEdit = () => {
    setSubcategoryName("");
    setSelectedCategory("");
    setEditingSubcategoryId(null);
  };

  const confirmDeleteSubcategory = async () => {
    if (!deleteSubTarget) return;
    try {
      setDeleteSubLoading(true);
      await deleteScraperSubcategory(deleteSubTarget.id);
      toast.success("Subcategory deleted");
      setDeleteSubTarget(null);
      await loadData();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleteSubLoading(false);
    }
  };

  // ── Table column definitions ──────────────────────────────────────────────
  const categoryColumns = [
    { key: "id",   label: "ID"   },
    { key: "name", label: "Name" },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <div className="flex gap-3">
          <button
            className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
            onClick={() => {
              setEditingCategoryId(row.id);
              setCategoryName(row.name);
              // scroll form into view on mobile
              document.getElementById("category-name-input")?.focus();
            }}
          >
            Edit
          </button>
          <button
            className="text-sm font-medium text-red-500 hover:text-red-700 transition-colors"
            onClick={() => setDeleteCatTarget({ id: row.id, name: row.name })}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  // Subcategory columns: Subcat first, Category second (per requirement)
  const subcategoryColumns = [
    { key: "id",   label: "ID" },
    { key: "name", label: "Subcategory" },                             // ← renamed, left
    { key: "category_name", label: "Category" },                  // ← moved right
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <div className="flex gap-3">
          <button
            className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
            onClick={() => {
              setEditingSubcategoryId(row.id);
              setSubcategoryName(row.name);
              setSelectedCategory(row.category_id);
              document.getElementById("subcategory-name-input")?.focus();
            }}
          >
            Edit
          </button>
          <button
            className="text-sm font-medium text-red-500 hover:text-red-700 transition-colors"
            onClick={() => setDeleteSubTarget({ id: row.id, name: row.name })}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="container mx-auto px-4 py-6 max-w-5xl space-y-6">

      <h1 className="text-2xl font-bold text-gray-900">Scraper Categories</h1>

      {/* ══════════════════════════════════════════
          CATEGORIES
      ══════════════════════════════════════════ */}
      <SectionCard
        icon="📁"
        title={editingCategoryId ? "Edit Category" : "Categories"}
      >
        {/* Form row */}
        <div className="flex flex-col sm:flex-row gap-3 items-end mb-6">
          <AppInput
            id="category-name-input"
            label="Category name"
            required
            placeholder="e.g. Fine Art"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            className="flex-1"
          />
          <div className="flex gap-2 items-end">
            <AppButton
              variant="primary"
              onClick={handleSaveCategory}
              loading={saveCatLoading ? (editingCategoryId ? "Updating…" : "Adding…") : false}
              disabled={saveCatLoading}
            >
              {editingCategoryId ? "Update Category" : "Add Category"}
            </AppButton>
            {editingCategoryId && (
              <AppButton variant="ghost" onClick={handleCancelCategoryEdit}>
                Cancel
              </AppButton>
            )}
          </div>
        </div>

        {/* Table */}
        <DataTable
          columns={categoryColumns}
          rows={categories.map((c) => ({ ...c, __key: c.id }))}
          emptyText={pageLoading ? "Loading…" : "No categories yet — add one above"}
        />
      </SectionCard>

      {/* ══════════════════════════════════════════
          SUBCATEGORIES
      ══════════════════════════════════════════ */}
      <SectionCard
        icon="🗂️"
        title={editingSubcategoryId ? "Edit Subcategory" : "Subcategories"}
      >
        {/* Form row */}
        <div className="flex flex-col sm:flex-row gap-3 items-end mb-6">
          <AppSelect
            id="subcat-category-select"
            label="Parent category"
            placeholder="Select category…"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
            className="sm:w-56"
          />
          <AppInput
            id="subcategory-name-input"
            label="Subcategory name"
            required
            placeholder="e.g. Oil Paintings"
            value={subcategoryName}
            onChange={(e) => setSubcategoryName(e.target.value)}
            className="flex-1"
          />
          <div className="flex gap-2 items-end">
            <AppButton
              variant="primary"
              onClick={handleSaveSubcategory}
              loading={saveSubLoading ? (editingSubcategoryId ? "Updating…" : "Adding…") : false}
              disabled={saveSubLoading}
            >
              {editingSubcategoryId ? "Update Subcategory" : "Add Subcategory"}
            </AppButton>
            {editingSubcategoryId && (
              <AppButton variant="ghost" onClick={handleCancelSubEdit}>
                Cancel
              </AppButton>
            )}
          </div>
        </div>

        {/* Table — Subcat column left, Category column right */}
        <DataTable
          columns={subcategoryColumns}
          rows={subcategories.map((s) => ({ ...s, __key: s.id }))}
          emptyText={pageLoading ? "Loading…" : "No subcategories yet — add one above"}
        />
      </SectionCard>

      {/* ── Confirm delete — Category ── */}
      <ConfirmModal
        isOpen={Boolean(deleteCatTarget)}
        title="Delete category?"
        message={`"${deleteCatTarget?.name}" and all its subcategory associations will be removed. This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        confirmLoading={deleteCatLoading ? "Deleting…" : false}
        onConfirm={confirmDeleteCategory}
        onCancel={() => setDeleteCatTarget(null)}
      />

      {/* ── Confirm delete — Subcategory ── */}
      <ConfirmModal
        isOpen={Boolean(deleteSubTarget)}
        title="Delete subcategory?"
        message={`"${deleteSubTarget?.name}" will be permanently deleted.`}
        confirmLabel="Delete"
        variant="danger"
        confirmLoading={deleteSubLoading ? "Deleting…" : false}
        onConfirm={confirmDeleteSubcategory}
        onCancel={() => setDeleteSubTarget(null)}
      />
    </div>
  );
};

export default ScraperCategories;